import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useLuxeReveal } from '@gjl/motion/css'
import { Mail, MapPin, Shield, CheckCircle } from 'lucide-react'
import { submitContactForm } from '@/lib/contact-api'

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' })
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const heroRef = useLuxeHero<HTMLDivElement>(0.2)
  const formRef = useLuxeReveal<HTMLDivElement>('.reveal')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')

    try {
      // Posts to the ABSOLUTE API origin and requires the API's own
      // `{ success: true }` envelope. Both halves matter: the relative
      // `/api/contact` this replaces never left CloudFront, and `response.ok`
      // was true anyway because the SPA fallback answers 200. See
      // `lib/contact-api.ts` for the full account.
      await submitContactForm(form)
      setStatus('success')
      setForm({ name: '', email: '', message: '' })
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="bg-gradient-dark-gold absolute inset-0" />
        <div ref={heroRef} className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <p className="text-primary mb-6 text-lg font-medium tracking-wide">Get in touch</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">Join the Investor List</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Reach out with questions or sign up to receive early access to upcoming investment opportunities.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={formRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-8 md:grid-cols-5 md:gap-12">
            {/* Form Column */}
            <div className="reveal md:col-span-3">
              {status === 'success' ? (
                <GlassCard accent className="py-12 text-center">
                  <CheckCircle className="text-primary mx-auto mb-6 h-16 w-16" />
                  <h2 className="text-foreground mb-4 text-2xl font-bold">Message Received</h2>
                  <p className="text-muted-foreground mb-6">
                    Thank you for reaching out! We'll be in touch within 1-2 business days.
                  </p>
                  <Button variant="outline" onClick={() => setStatus('idle')}>
                    Send Another Message
                  </Button>
                </GlassCard>
              ) : (
                <GlassCard>
                  <h2 className="text-foreground mb-6 text-2xl font-semibold">Send a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="name" className="text-foreground mb-2 block text-sm font-medium">
                        Name
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="bg-input border-border focus:border-primary focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="text-foreground mb-2 block text-sm font-medium">
                        Email
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="you@example.com"
                        className="bg-input border-border focus:border-primary focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="text-foreground mb-2 block text-sm font-medium">
                        Message
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={form.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your investment goals or ask a question..."
                        className="bg-input border-border focus:border-primary focus:ring-primary"
                      />
                    </div>

                    {status === 'error' && (
                      <div className="text-destructive bg-destructive/10 rounded-lg p-3 text-sm">
                        Something went wrong. Please try again.
                      </div>
                    )}

                    <Button type="submit" size="lg" className="w-full" disabled={status === 'loading'}>
                      {status === 'loading' ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </GlassCard>
              )}
            </div>

            {/* Info Column */}
            <div className="reveal space-y-6 md:col-span-2">
              <GlassCard className="gold-accent-left pl-6">
                <div className="flex items-start gap-4">
                  <Mail className="text-primary mt-1 h-5 w-5 shrink-0" />
                  <div>
                    <h3 className="text-foreground mb-1 font-semibold">Email</h3>
                    <a
                      href="mailto:garrett@fairmeadow.capital"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      garrett@fairmeadow.capital
                    </a>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="gold-accent-left pl-6">
                <div className="flex items-start gap-4">
                  <MapPin className="text-primary mt-1 h-5 w-5 shrink-0" />
                  <div>
                    <h3 className="text-foreground mb-1 font-semibold">Investment Focus</h3>
                    <p className="text-muted-foreground">
                      Cleveland, Ohio
                      <br />
                      Small Multifamily
                    </p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="gold-accent-left pl-6">
                <div className="flex items-start gap-4">
                  <Shield className="text-primary mt-1 h-5 w-5 shrink-0" />
                  <div>
                    <h3 className="text-foreground mb-1 font-semibold">What to Expect</h3>
                    <ul className="text-muted-foreground space-y-2 text-sm">
                      <li>Response within 1-2 business days</li>
                      <li>No spam - only deal alerts and updates</li>
                      <li>Direct communication from the founder</li>
                    </ul>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
